import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Apiurl from "../../environment/Apiurl";
import axios from "axios";
import { Trash2, Edit, Search } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";

function SalaryDetails() {
  const navigate = useNavigate();

  // State
  const [responseData, setResponseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showEntries, setShowEntries] = useState("10");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError("No role assigned to the user. Please contact the administrator.");
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid or empty roles data received");
      }

      const roleData = response.data.find((role) => role.role_id === userRoleId);

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          // Handle typo: priviallages or privileges
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error("Error fetching user privileges:", error.message, error.response?.data);
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Salary Details page
  const hasPermission = (permission) => {
    const salaryPrivileges = userPrivileges["Salary Details"];
    if (!salaryPrivileges) {
      console.log("No privileges found for Salary Details page");
      return false;
    }
    console.log(`Checking permission '${permission}' for Salary Details:`, salaryPrivileges);
    if (salaryPrivileges.all) return true;
    const hasPerm = salaryPrivileges[permission] || false;
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  // Fetch salary details data
  const fetchResponse = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Salary Details page");
      setError("You do not have permission to view salary details.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/salary-details`);
      console.log("Salary details fetched:", response.data);
      setResponseData(response.data || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching salary details:", error.message, error.response?.data);
      setError("Failed to fetch salary details. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete salary detail
  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Salary Details page");
      setError("You do not have permission to delete salary details.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid salary detail ID:", deleteId);
      setError("Invalid salary detail ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting salary detail with ID:", deleteId);
      await axios.delete(`${Apiurl}/salary-details/${deleteId}`);
      fetchResponse();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting salary detail:", error.message, error.response?.data);
      setError("Failed to delete salary detail. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Salary Details page");
      setError("You do not have permission to delete salary details.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid salary detail ID:", id);
      setError("Invalid salary detail ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Salary Detail modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Salary Detail modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

      useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

  // Handle edit
  const handleEdit = (id) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Salary Details page");
      setError("You do not have permission to edit salary details.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid salary detail ID:", id);
      setError("Invalid salary detail ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Navigating to edit salary detail ID:", id);
    navigate(`/hr-management/salary-details/update-salary-details/${id}`);
  };

  // Handle add
  const handleAddSalaryDetails = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Salary Details page");
      setError("You do not have permission to add salary details.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Navigating to add salary details page");
    navigate("/hr-management/salary-details/add-salary-details");
  };

  // Fetch privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    console.log("Checking read permission after privileges loaded:", userPrivileges);
    if (hasPermission("read")) {
      console.log("Fetching salary details due to read permission");
      fetchResponse();
    } else if (!loadingPrivileges) {
      console.warn("Read permission denied for Salary Details page");
      setError("You do not have permission to view salary details.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Reset current page on search or items per page change
  useEffect(() => {
    console.log("Resetting current page due to searchTerm or itemsPerPage change");
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Filter data with useMemo
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.staff_id,
        item.first_name,
        item.mobile_no,
        item.department,
        item.status,
      ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    console.log("Filtered data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  if (loadingPrivileges) {
    return <div className="p-3">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Salary Details page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom { 
          background-color: #01AEBE; 
          color: white; 
          border: none;
        }
        .btn-custom:hover { 
          background-color: #018A97; 
          color: white;
        }
      `}</style>

      {hasPermission("create") && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <button
            className="btn btn-custom btn-sm"
            onClick={handleAddSalaryDetails}
          >
            <i className="fas fa-plus me-2"></i> Add Salary Details
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log("Changing items per page to:", e.target.value);
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="10">Show 10</option>
                  <option value="20">Show 20</option>
                  <option value="50">Show 50</option>
                  <option value="100">Show 100</option>
                  <option value="200">Show 200</option>
                  <option value="500">Show 500</option>
                  <option value="1000">Show 1000</option>
                  <option value="All">Show All</option>
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search by ID, name, mobile, dept, status"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Staff ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Department</th>
                  <th>Status</th>
                  {(hasPermission("update") || hasPermission("delete")) && (
                    <th>Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item._id || index} className="align-middle">
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{item.staff_id || "-"}</td>
                      <td>{item.first_name || "-"}</td>
                      <td>{item.mobile_no || "-"}</td>
                      <td>{item.department || "-"}</td>
                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center ${
                            item.status?.toLowerCase() === "active"
                              ? "bg-success text-white"
                              : "bg-danger text-white"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              item.status?.toLowerCase() === "active"
                                ? faCheckCircle
                                : faTimesCircle
                            }
                            className="me-2"
                          />
                          {item.status?.toLowerCase() === "active"
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>
                      {(hasPermission("update") || hasPermission("delete")) && (
                        <td>
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              style={{ backgroundColor: "transparent" }}
                              onClick={() => openDeleteModal(item._id)}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              style={{ backgroundColor: "transparent" }}
                              onClick={() => handleEdit(item._id)}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        hasPermission("update") || hasPermission("delete") ? 7 : 6
                      }
                      className="text-center py-4"
                    >
                      {loading ? "Loading..." : "No salary details found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="container">
            <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => currentPage > 1 && handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i>
                <span className="d-none d-sm-inline">Previous</span>
              </button>

              <div
                className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                style={{ maxWidth: "100%" }}
              >
                {pages.map((page, index) => (
                  <button
                    key={index}
                    className={`btn btn-sm px-3 flex-shrink-0 ${
                      page === currentPage ? "text-white" : "btn-outline-secondary"
                    }`}
                    style={
                      page === currentPage
                        ? { backgroundColor: "#20c997", border: "none" }
                        : {}
                    }
                    onClick={() => handlePageClick(page)}
                    disabled={page === "..."}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() =>
                  currentPage < totalPages && handlePageClick(currentPage + 1)
                }
                disabled={currentPage === totalPages}
              >
                <span className="d-none d-sm-inline">Next</span>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteSalaryModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteSalaryModalLabel">
                  Delete Salary Detail
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this salary detail?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && hasPermission("delete") && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default SalaryDetails;